import { privatePoolDeploymentBlock } from "./privatePools";
import { ethers } from "ethers";
import {
  SHIELDED_POOL_JOINSPLIT_V2_INTERFACE,
  buildFlatJoinSplitV2Transaction,
  buildFlatMigrateV2Transaction,
  buildFlatPrivateBridgeV2Transaction,
  buildFlatPrivateSwapV2Transaction,
  buildFlatWithdrawBatchV2Transaction,
  buildFlatWithdrawV2Transaction,
  validateFlatJoinSplitV2WithdrawBatchEntries,
  computeFlatJoinSplitV2BridgeAuxHash,
  computeFlatJoinSplitV2SwapAuxHash,
  proveFlatJoinSplitV2,
  readFlatJoinSplitV2PoolSnapshot,
  resolveFlatJoinSplitV2Action,
  selectFlatJoinSplitV2Arity,
  simulateFlatJoinSplitV2Transaction,
  type FlatJoinSplitV2BridgeData,
  type FlatJoinSplitV2Hex32,
  type FlatJoinSplitV2HexData,
  type FlatJoinSplitV2InputNoteWitness,
  type FlatJoinSplitV2OutputNoteWitness,
  type FlatJoinSplitV2ProveRequest,
  type FlatJoinSplitV2SwapData,
} from "./sdk";
import { EVM_CONFIG } from "../config";
import { getBaseReadProvider } from "../utils/base-read-provider";
import {
  submitBasePoolRelay,
  serializeTransaction,
} from "../utils/relayer-client";
import { computeNPK, computeNullifier } from "../joinsplit/keys";
import type {
  JoinSplitNote,
  SpendingKeys,
  StoredJoinSplitNote,
} from "../joinsplit/types";
import { readSpendTreeHistory } from "./balanceTreeHistory";
import type {
  FlatJoinSplitV2MerklePath,
  FlatJoinSplitV2TreeScanResult,
} from "./merkleIndex";
import {
  assertFlatJoinSplitV2Note,
  createFlatJoinSplitV2Note,
  flatFieldHex,
} from "./notes";
import { withFlatJoinSplitV2Prover } from "./prover";
import { assertAscendingLeafIndices, selectForAmount } from "./spendSelection";
import {
  markFlatJoinSplitV2SettlementBroadcast,
  markFlatJoinSplitV2SettlementFinalized,
  markFlatJoinSplitV2SettlementIncluded,
  prepareFlatJoinSplitV2SettlementJournal,
  type FlatJoinSplitV2JournalDelivery,
  type FlatJoinSplitV2JournalKind,
  type FlatJoinSplitV2JournalScope,
} from "./pendingJournal";
import { fail } from "./common";

export const POOL_READ_ABI = [
  ...SHIELDED_POOL_JOINSPLIT_V2_INTERFACE.fragments,
  "function commitmentInserted(bytes32) view returns (bool)",
  "event LeafInsertedInTree(uint256 indexed treeIndex,bytes32 leaf,uint256 leafIndex)",
] as const;
export const POOL_INTERFACE = new ethers.Interface(POOL_READ_ABI);
export const LEAF_EVENT = POOL_INTERFACE.getEvent("LeafInsertedInTree")!;

export interface LoadedTree {
  treeIndex: number;
  root: `0x${string}`;
  path(commitment: string): FlatJoinSplitV2MerklePath;
}

export function requireEnabled(): string {
  if (!EVM_CONFIG.JOINSPLIT_V2.ENABLED) {
    fail("the fresh-pool settlement stack is disabled");
  }
  const pool = EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT;
  if (!pool || !ethers.isAddress(pool))
    fail("fresh pool address is not configured");
  if (EVM_CONFIG.JOINSPLIT_V2.DEPLOYMENT_BLOCK <= 0) {
    fail("fresh pool deployment block is not configured");
  }
  return ethers.getAddress(pool);
}

export async function requireProvider(
  signer: ethers.Signer,
): Promise<{ provider: ethers.Provider; chainId: bigint }> {
  const provider = signer.provider;
  if (!provider) fail("connected signer has no provider");
  const network = await provider.getNetwork();
  const chainId = BigInt(network.chainId);
  if (chainId !== BigInt(EVM_CONFIG.CHAIN_ID)) {
    fail(`wallet is on chain ${chainId}; expected ${EVM_CONFIG.CHAIN_ID}`);
  }
  return { provider, chainId };
}

export function pinnedReadProvider(provider: ethers.Provider) {
  return {
    getBlockNumber: () => provider.getBlockNumber(),
    getBlock: (blockTag: number) => provider.getBlock(blockTag),
    getNetwork: () => provider.getNetwork(),
    // Accept the v6 request shape, retaining the positional fallback while
    // older pinned SDK artifacts are rolled forward.
    call: (
      transaction: { to: string; data: string; blockTag?: number },
      legacyBlockTag?: number,
    ) => {
      const blockTag = transaction.blockTag ?? legacyBlockTag;
      if (
        typeof blockTag !== "number" ||
        !Number.isSafeInteger(blockTag) ||
        blockTag < 0
      ) {
        fail("pool snapshot calls must specify an exact block number");
      }
      return provider.call({ ...transaction, blockTag });
    },
  };
}

export async function readAtBlock(
  provider: ethers.Provider,
  pool: string,
  name: string,
  args: readonly unknown[],
  blockTag: number,
): Promise<ethers.Result> {
  const data = POOL_INTERFACE.encodeFunctionData(name, args);
  const result = await provider.call({ to: pool, data, blockTag } as any);
  return POOL_INTERFACE.decodeFunctionResult(name, result);
}

export async function readCurrentTokenDomain(
  provider: ethers.Provider,
  pool: string,
  tokenAddress: string,
): Promise<{ blockTag: number; treeIndex: number; tokenHash: `0x${string}` }> {
  const blockTag = await provider.getBlockNumber();
  const before = await provider.getBlock(blockTag);
  if (!before?.hash) fail("could not pin the current pool block");
  const [treeResult, tokenResult] = await Promise.all([
    readAtBlock(provider, pool, "currentTreeIndex", [], blockTag),
    readAtBlock(provider, pool, "currentTokenHash", [tokenAddress], blockTag),
  ]);
  const treeIndex = Number(treeResult[0]);
  const tokenHash = String(tokenResult[0]);
  if (!Number.isSafeInteger(treeIndex) || treeIndex < 0) {
    fail("pool returned an invalid current tree index");
  }
  if (!/^0x[0-9a-fA-F]{64}$/.test(tokenHash)) {
    fail("pool returned a malformed current token hash");
  }
  const after = await provider.getBlock(blockTag);
  if (!after?.hash || after.hash.toLowerCase() !== before.hash.toLowerCase()) {
    fail("current pool block changed during the pinned token-domain read");
  }
  return {
    blockTag,
    treeIndex,
    tokenHash: tokenHash.toLowerCase() as `0x${string}`,
  };
}

const treeLoads = new WeakMap<
  ethers.Provider,
  Map<string, Promise<LoadedTree>>
>();

function loadedTree(result: FlatJoinSplitV2TreeScanResult): LoadedTree {
  return {
    treeIndex: result.treeIndex,
    root: result.root as `0x${string}`,
    path(commitment: string): FlatJoinSplitV2MerklePath {
      const location = result.tree.locate(commitment);
      if (!location)
        fail(`commitment ${commitment} is not in tree ${result.treeIndex}`);
      return result.tree.path(location.leafIndex);
    },
  };
}

export async function loadTree(
  provider: ethers.Provider,
  pool: string,
  treeIndex: number,
): Promise<LoadedTree> {
  const key = `${pool.toLowerCase()}:${treeIndex}:${EVM_CONFIG.JOINSPLIT_V2.DEPLOYMENT_BLOCK}`;
  let pending = treeLoads.get(provider);
  if (!pending) {
    pending = new Map();
    treeLoads.set(provider, pending);
  }
  if (pending.has(key)) return pending.get(key)!;
  const work = loadTreeAtPinnedBlock(provider, pool, treeIndex);
  pending.set(key, work);
  try {
    return await work;
  } finally {
    if (pending.get(key) === work) pending.delete(key);
  }
}

async function loadTreeAtPinnedBlock(
  provider: ethers.Provider,
  pool: string,
  treeIndex: number,
): Promise<LoadedTree> {
  const toBlock = await provider.getBlockNumber();
  const pin = await provider.getBlock(toBlock);
  if (!pin?.hash) fail("could not pin witness history");
  const currentTree = Number(
    (await readAtBlock(provider, pool, "currentTreeIndex", [], toBlock))[0],
  );
  let expectedRoot: string | undefined;
  let expectedNextLeafIndex: number | undefined;
  if (treeIndex === currentTree) {
    const [root, count] = await Promise.all([
      readAtBlock(provider, pool, "merkleRoot", [], toBlock),
      readAtBlock(provider, pool, "nextLeafIndex", [], toBlock),
    ]);
    expectedRoot = String(root[0]);
    expectedNextLeafIndex = Number(count[0]);
  }
  // Share the public checkpoint already verified by note sync and recovery.
  // This reader rechecks the old prefix and the supplied pin before/after
  // scanning, validates root/count, and never caches a failed continuation.
  const result = await readSpendTreeHistory({
    provider: provider as any,
    pool,
    treeIndex,
    fromBlock: privatePoolDeploymentBlock(pool),
    toBlock,
    expectedToBlockHash: pin.hash,
    chunkSize: Math.min(EVM_CONFIG.JOINSPLIT_V2.LOG_CHUNK_SIZE, 5_000),
    expectedRoot,
    expectedNextLeafIndex,
  });
  return loadedTree(result);
}

/** Witness history uses the app RPC; wallet checks still use the signer. */
export async function loadSpendTree(
  chainId: bigint,
  pool: string,
  treeIndex: number,
): Promise<LoadedTree> {
  const provider = getBaseReadProvider();
  const network = await provider.getNetwork();
  if (
    chainId !== BigInt(EVM_CONFIG.CHAIN_ID) ||
    BigInt(network.chainId) !== chainId
  ) {
    fail("witness RPC is on the wrong chain");
  }
  return loadTree(provider, pool, treeIndex);
}
